<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  HTTP_METHODS,
  RULE_OPERATORS,
} from '../../../constants/csmlFieldOptions';

const props = defineProps({
  node: { type: Object, required: true },
});

const emit = defineEmits(['patch']);

const BODY_METHODS = ['POST', 'PUT', 'PATCH'];

const { t } = useI18n();

const operatorOptions = RULE_OPERATORS.map(option => ({
  value: option.value,
  label: t(`AGENT_BOTS.VISUAL_BUILDER.${option.labelKey}`),
}));

const sendsBody = computed(() =>
  BODY_METHODS.includes((props.node.apiMethod || 'GET').toUpperCase())
);

const jsonObjectError = raw => {
  const text = String(raw ?? '').trim();
  if (!text) return '';
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'object';
    }
    return '';
  } catch {
    return 'json';
  }
};

const rules = () => props.node.responseRules || [];

const updateRule = (index, patch) => {
  emit('patch', {
    responseRules: rules().map((rule, i) =>
      i === index ? { ...rule, ...patch } : rule
    ),
  });
};

const removeRule = index => {
  emit('patch', { responseRules: rules().filter((_, i) => i !== index) });
};

const addRule = () => {
  const count = rules().length + 1;
  emit('patch', {
    responseRules: [
      ...rules(),
      {
        label: `${t('AGENT_BOTS.VISUAL_BUILDER.RULE')} ${count}`,
        field: 'status',
        operator: 'equals',
        value: '200',
      },
    ],
  });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <Input
      :model-value="node.apiUrl"
      size="sm"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_URL')"
      @update:model-value="emit('patch', { apiUrl: $event })"
    />
    <div class="flex flex-col gap-1">
      <span class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.METHOD') }}
      </span>
      <Select
        :model-value="node.apiMethod || 'GET'"
        :options="HTTP_METHODS"
        @update:model-value="emit('patch', { apiMethod: $event })"
      />
    </div>
    <TextArea
      :model-value="node.apiHeaders"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_HEADERS')"
      :placeholder="
        t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_HEADERS_PLACEHOLDER')
      "
      :message="
        jsonObjectError(node.apiHeaders)
          ? t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_HEADERS_INVALID')
          : t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_HEADERS_HINT')
      "
      :message-type="jsonObjectError(node.apiHeaders) ? 'error' : 'info'"
      :max-length="4000"
      auto-height
      @update:model-value="emit('patch', { apiHeaders: $event })"
    />
    <TextArea
      v-if="sendsBody"
      :model-value="node.apiBody"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_BODY')"
      :placeholder="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_BODY_PLACEHOLDER')"
      :message="
        jsonObjectError(node.apiBody)
          ? t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_BODY_INVALID')
          : t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_BODY_HINT')
      "
      :message-type="jsonObjectError(node.apiBody) ? 'error' : 'info'"
      :max-length="4000"
      auto-height
      @update:model-value="emit('patch', { apiBody: $event })"
    />
    <template v-if="node.callStyle === 'http.post'">
      <Input
        :model-value="node.eventMemory"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.EVENT_MEMORY')"
        @update:model-value="emit('patch', { eventMemory: $event })"
      />
      <TextArea
        :model-value="node.errorMessage"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.ERROR_MESSAGE')"
        :max-length="2000"
        @update:model-value="emit('patch', { errorMessage: $event })"
      />
      <Input
        :model-value="node.responseField"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.RESPONSE_FIELD')"
        @update:model-value="emit('patch', { responseField: $event })"
      />
    </template>
    <TextArea
      v-else
      :model-value="node.content"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_MESSAGE')"
      :message="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.API_MESSAGE_HINT')"
      :max-length="2000"
      @update:model-value="emit('patch', { content: $event })"
    />
    <div
      v-for="(rule, idx) in node.responseRules || []"
      :key="`rule-${idx}`"
      class="flex flex-col gap-2 p-2 border rounded-lg border-n-weak"
    >
      <div class="flex items-center justify-between">
        <span class="text-[10px] font-semibold uppercase text-n-slate-10">
          {{ t('AGENT_BOTS.VISUAL_BUILDER.RULE') }} {{ idx + 1 }}
        </span>
        <Button
          size="xs"
          variant="ghost"
          color="ruby"
          icon="i-lucide-trash-2"
          is-icon-only
          :title="t('AGENT_BOTS.VISUAL_BUILDER.REMOVE')"
          @click="removeRule(idx)"
        />
      </div>
      <Input
        :model-value="rule.label"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.RULE_LABEL')"
        @update:model-value="updateRule(idx, { label: $event })"
      />
      <Input
        :model-value="rule.field"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.FIELD')"
        @update:model-value="updateRule(idx, { field: $event })"
      />
      <div class="flex flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.OPERATOR') }}
        </span>
        <Select
          :model-value="rule.operator || 'equals'"
          :options="operatorOptions"
          @update:model-value="updateRule(idx, { operator: $event })"
        />
      </div>
      <Input
        v-if="rule.operator !== 'exists'"
        :model-value="rule.value"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.VALUE')"
        @update:model-value="updateRule(idx, { value: $event })"
      />
    </div>
    <Button
      size="sm"
      color="blue"
      variant="faded"
      icon="i-lucide-plus"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.ADD_RULE')"
      @click="addRule"
    />
  </div>
</template>
